<script setup lang="ts">
  import { StockItemsService } from '@/api/services/stock-items.service';
  import ModalsStockAdjust from '@/components/modals/stock/ModalsStockAdjust.vue';
  import ModalsStockItem from '@/components/modals/stock/ModalsStockItem.vue';
  import RestaurantTierChip from '@/components/partials/RestaurantTierChip.vue';
  import { useModal } from '@/composition/use-modal';
  import { useSocket } from '@/composition/use-socket';
  import { useUi } from '@/composition/use-ui';
  import {
    STOCK_CHANGED_EVENT,
    stockChangeConcernsStockItems,
  } from '@/domain/catalog/catalog-events';
  import type { IStockChangedEvent } from '@/domain/catalog/catalog-events';
  import { StockItemType } from '@/enums/StockItemType';
  import { STOCK_UNIT_LABELS } from '@/enums/StockUnit';
  import { IDataOptions } from '@/interfaces/IDataOptions';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { EventBus } from '@/modules/EventBus';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { onMounted, onUnmounted, reactive, ref, watch } from 'vue';

  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const modals = useModal();
  const socket = useSocket();

  const stockItems = ref<IStockItem[] | null>(null);

  const selectedType = ref<StockItemType | null>(null);

  const paginationData = ref<IDataOptions>({
    page: 1,
    itemsPerPage: 10,
    mustSort: true,
    sortBy: ['name'],
    sortDesc: [false],
  });

  const totalItems = ref(0);

  const loading = ref(false);
  async function loadStockItems() {
    if (!restaurantStore.restaurant.id || !restaurantStore.selectedBranch.id)
      return;

    try {
      loading.value = true;
      const response = await StockItemsService.getAll({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        type: selectedType.value,
        options: paginationData.value,
      });

      stockItems.value = response.items;
      totalItems.value = response.total;
    } catch (error) {
      ui.serverError(error, 'Error al cargar el stock');
    } finally {
      loading.value = false;
    }
  }

  watch(
    [
      () => restaurantStore.restaurant.id,
      () => restaurantStore.selectedBranch.id,
      selectedType,
      paginationData,
    ],
    () => {
      loadStockItems();
    },
    { immediate: true }
  );

  const typeTabs = [
    {
      title: 'Todo',
      value: null,
    },
    {
      title: 'Ingredientes',
      value: StockItemType.Ingredient,
    },
    {
      title: 'Productos',
      value: StockItemType.Product,
    },
  ];

  const headers = [
    { text: 'Nombre', value: 'name' },
    { text: 'Cantidad actual', value: 'quantity' },
    { text: 'Acciones', value: 'actions', sortable: false },
  ];

  function formatQuantity(quantity: number, unit: string) {
    return `${quantity} ${unit}`;
  }

  function openAdjustStockDialog(item: IStockItem) {
    modals.show(
      ModalsStockAdjust,
      { item },
      {
        name: 'stock-adjust',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {}
    );
  }

  interface IStockAdjusted {
    branchId: number;
    id: number;
    quantity: number;
  }

  function onStockAdjusted(data: IStockAdjusted) {
    if (data.branchId !== restaurantStore.selectedBranch.id) return;

    const item = stockItems.value?.find((item) => item.id === data.id);
    if (!item) return;

    item.quantity = data.quantity;
  }

  /**
   * The assistant panel changed this branch's stock items (created,
   * renamed, re-costed or moved, plan P5-3): the list is read again, as
   * after a change made here.
   */
  function onAssistantStockChanged(change: IStockChangedEvent) {
    const restaurantId = restaurantStore.restaurant.id;
    const branchId = restaurantStore.selectedBranch.id;
    if (!restaurantId || !branchId) return;
    if (!stockChangeConcernsStockItems(change, { restaurantId, branchId }))
      return;
    loadStockItems();
  }

  onMounted(() => {
    socket.socket.value.on(
      `r:${restaurantStore.restaurant.id}:stockQuantity`,
      onStockAdjusted
    );
    EventBus.$on(STOCK_CHANGED_EVENT, onAssistantStockChanged);
  });

  onUnmounted(() => {
    socket.socket.value.off(
      `r:${restaurantStore.restaurant.id}:stockQuantity`,
      onStockAdjusted
    );
    EventBus.$off(STOCK_CHANGED_EVENT, onAssistantStockChanged);
  });

  function openNewStockItemDialog() {
    modals.show(
      ModalsStockItem,
      {},
      {
        name: 'stock-item',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: (item: IStockItem) => {
          loadStockItems();
        },
      }
    );
  }

  function openEditStockItemDialog(item: IStockItem) {
    modals.show(
      ModalsStockItem,
      { itemToEdit: item },
      {
        name: 'stock-item',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        saved: (item: IStockItem) => {
          loadStockItems();
        },
      }
    );
  }

  const deletingItemId = ref<number | null>(null);
  async function deleteStockItem(item: IStockItem) {
    if (deletingItemId.value) return;

    const confirmed = await modals.confirm({
      title: 'Eliminar item',
      message: '¿Está seguro de que desea eliminar este item?',
      acceptText: 'Eliminar',
      cancelText: 'Cancelar',
      acceptColor: 'error',
    });

    if (!confirmed) return;

    try {
      deletingItemId.value = item.id;
      await StockItemsService.delete(item.id);
      loadStockItems();
    } catch (error) {
      ui.serverError(error, 'Error al eliminar el item');
    } finally {
      deletingItemId.value = null;
    }
  }
</script>

<template>
  <div class="stock-page">
    <div class="stock-page--inner">
      <v-card class="stock-page--card">
        <v-card-title>
          <div class="header-container">
            <div class="header-title-container">
              <v-icon>mdi-food-fork-drink</v-icon>
              <span>Administrar stock</span>
              <RestaurantTierChip :tier="1" />
              <v-chip label color="amber" x-small>BETA</v-chip>
            </div>
            <div class="header-actions-container">
              <v-btn color="success" @click="openNewStockItemDialog">
                <v-icon left>mdi-plus</v-icon>
                <span>Nuevo</span>
              </v-btn>
            </div>
          </div>
        </v-card-title>
        <v-card-subtitle>
          Desde aquí puede gestionar los componentes de inventario. Para
          asignar componentes a un item del menú, puede hacerlo desde la
          pantalla de edición de Items, pestaña Stock.
        </v-card-subtitle>
        <v-card-text>
          <div class="stock-page--card-content">
            <div class="type-tabs">
              <v-btn
                v-for="tab in typeTabs"
                :key="tab.value"
                depressed
                small
                @click="selectedType = tab.value"
                :color="selectedType === tab.value ? 'primary' : 'default'"
                >{{ tab.title }}</v-btn
              >
            </div>
            <div class="stock-items">
              <template v-if="!stockItems">
                <v-skeleton-loader type="table-row" />
                <v-skeleton-loader type="table-row" />
                <v-skeleton-loader type="table-row" />
              </template>
              <template v-else-if="stockItems.length === 0">
                <p class="tw-text-center tw-text-gray-500">
                  No hay items de inventario. Puede crearlos en la pantalla de
                  edición de Items, pestaña Stock.
                </p>
              </template>
              <template v-else>
                <v-data-table
                  dense
                  :items="stockItems"
                  :headers="headers"
                  :options.sync="paginationData"
                  :server-items-length="totalItems"
                  :footer-props="{
                    'items-per-page-options': [10, 20, 50, 100, 200],
                  }"
                  :loading="loading"
                >
                  <template v-slot:item.quantity="{ item }">
                    {{ formatQuantity(item.quantity, item.unit) }}
                  </template>
                  <template v-slot:item.unit="{ item }">
                    {{ STOCK_UNIT_LABELS[item.unit] }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <div class="actions">
                      <v-btn
                        x-small
                        depressed
                        color="primary"
                        @click="openAdjustStockDialog(item)"
                      >
                        <v-icon left small>mdi-plus-minus-variant</v-icon>
                        <span>Ajuste</span>
                      </v-btn>
                      <v-btn icon small @click="openEditStockItemDialog(item)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        small
                        @click="deleteStockItem(item)"
                        :loading="deletingItemId === item.id"
                        :disabled="!!deletingItemId"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .stock-page {
    @apply tw-flex tw-justify-center tw-items-center tw-h-full tw-py-4;

    &--inner {
      @apply tw-w-full tw-max-w-7xl;

      .stock-page--card {
        @apply tw-shadow-none tw-rounded-xl;

        .header-container {
          @apply tw-flex tw-justify-between tw-items-center tw-w-full;

          .header-title-container {
            @apply tw-flex tw-items-center tw-space-x-1.5;
          }
        }

        .stock-page--card-content {
          @apply tw-space-y-4;

          .type-tabs {
            @apply tw-flex tw-space-x-2;
          }

          .actions {
            @apply tw-space-x-2;
          }
        }
      }
    }
  }
</style>
